import React, { useCallback, useRef, useEffect, useState } from 'react';
import Webcam, { WebcamProps } from 'react-webcam';

import { Delay, SecondToMs } from 'src/helpers/delay';

export type DeviceInfo = {
  deviceId: string;
  video: {
    isFacingUser?: boolean;
    captureResolution?: {
      width: number;
      height: number;
    };
    displayResolution?: {
      width: number;
      height: number;
    };
  };
};

export enum ResolutionOptionType {
  AUTO,
  MAXIMUM,
  NEAREST,
}

export type AutoResolution = {
  type: ResolutionOptionType.AUTO;
};

export type MaximumResolution = {
  type: ResolutionOptionType.MAXIMUM;
};

export type NearestResolution = {
  type: ResolutionOptionType.NEAREST;
  width: number;
  height: number;
};

export type ImageCaptureResolutionHint = NearestResolution | MaximumResolution | AutoResolution;

export enum ImageMirrorBehavior {
  FLIP_WHEN_USER_FACING,
  NO_FLIPPING,
}

export type RenderProps = {
  renderFunctions: {
    drawCameraDisplay: () => React.ReactFragment;
  };
  commands: {
    switchDevice: () => void;
    refreshDeviceList: () => void;
    captureImage: () => void;
  };
  status: {
    DeviceInfo?: DeviceInfo;
    devicesTotalCount: number;
    isDeviceLoading: boolean;
    isRefreshingDeviceList: boolean;
  };
};

export type ImageCaptureProps = {
  onDeviceInfoChanged?: (DeviceInfo) => void;
  onImageCaptured?: (base64Image: string) => void;
  onDeviceAccessed?: () => void;
  onDeviceAccessDenied?: () => void;
  onDeviceNotFound?: () => void;
  imageDisplayResolution?: {
    width: number;
    height: number;
  };
  imageCaptureResolutionHint?: ImageCaptureResolutionHint;
  imageMirrorBehavior?: ImageMirrorBehavior;
  children?: (props: RenderProps) => JSX.Element;
};

export default function ImageCapture(props: ImageCaptureProps) {
  const webcamRef = useRef<Webcam>(null);
  const [, setCurrentDeviceIndex] = useState(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null);
  const [isDeviceLoading, setIsDeviceLoading] = useState(true);
  const [isDeviceListRefreshing, setIsDeviceListRefreshing] = useState(false);
  const [currentDeviceInfo, setCurrentDeviceInfo] = useState<DeviceInfo | null>(null);

  useEffect(() => {
    if (props.onDeviceInfoChanged) {
      props.onDeviceInfoChanged(currentDeviceInfo);
    }
  }, [currentDeviceInfo]);

  const getImageCaptureResolutionHint = () =>
    props.imageCaptureResolutionHint || {
      type: ResolutionOptionType.AUTO,
    };

  const handleDevices = useCallback(
    (mediaDevices: MediaDeviceInfo[]) => {
      const devicesNew = mediaDevices.filter(({ kind }) => kind === 'videoinput');
      setDevices(devicesNew);
    },
    [setDevices],
  );

  useEffect(() => {
    if (devices) {
      setCurrentDeviceInfo(null);
      if (devices.length > 0) {
        const deviceId = devices[0].deviceId;
        getCurrentDeviceInfo(deviceId, getImageCaptureResolutionHint()).then(
          (currentDeviceInfoNew) => {
            if (currentDeviceInfoNew) {
              setCurrentDeviceInfo(currentDeviceInfoNew);
            }
          },
        );
      } else if (props.onDeviceNotFound) {
        props.onDeviceNotFound();
      }
    }
  }, [devices]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const capture = useCallback(() => {
    if (webcamRef.current !== null && props.onImageCaptured) {
      const imageSrc = webcamRef.current.getScreenshot();
      //const base64 = imageSrc ? imageSrc.split(',')[1] : '';
      props.onImageCaptured(imageSrc ? imageSrc : '');
    }
  }, [webcamRef, props.onImageCaptured]);

  const getCurrentDeviceInfo = async (
    deviceId: string,
    imageCaptureResolution: ImageCaptureResolutionHint,
  ): Promise<DeviceInfo | undefined> => {
    const deviceInfo: DeviceInfo = {
      deviceId: deviceId,
      video: {},
    };

    let constraints: MediaStreamConstraints = {
      audio: false,
    };

    switch (imageCaptureResolution.type) {
      case ResolutionOptionType.MAXIMUM:
        constraints.video = {
          deviceId: deviceId,
          width: { ideal: 4096 },
          height: { ideal: 2160 },
        };
        break;
      case ResolutionOptionType.NEAREST:
        constraints.video = {
          deviceId: deviceId,
          width: { ideal: imageCaptureResolution.width },
          height: { ideal: imageCaptureResolution.height },
        };
        break;
      case ResolutionOptionType.AUTO:
        constraints.video = {
          deviceId: deviceId,
        };
        break;
    }

    return navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        const videoTracks = stream.getVideoTracks();
        if (videoTracks.length > 0) {
          // we don't except an empty array here
          const videoTrack = videoTracks[0];

          const videoTrackSettings = videoTrack.getSettings();
          if (videoTrackSettings.height !== undefined && videoTrackSettings.width !== undefined) {
            deviceInfo.video.captureResolution = {
              height: videoTrackSettings.height,
              width: videoTrackSettings.width,
            };
          }

          if (videoTrackSettings.facingMode !== undefined) {
            deviceInfo.video.isFacingUser = videoTrackSettings.facingMode.indexOf('user') >= 0;
          }

          stream.getTracks().forEach((track) => track.stop());
        }

        if (!props.children && props.onDeviceAccessed) {
          props.onDeviceAccessed();
        }

        return deviceInfo;
      })
      .catch(() => {
        if (props.onDeviceAccessDenied) {
          props.onDeviceAccessDenied();
        }
        return undefined;
      });
  };

  const refreshDeviceList = () => {
    setIsDeviceListRefreshing(true);
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
    Delay(SecondToMs(1)).then(() => setIsDeviceListRefreshing(false));
  };

  const cycleCamera = () => {
    if (devices) {
      setIsDeviceLoading(true);

      if (webcamRef?.current?.stream) {
        webcamRef.current.stream.getTracks().forEach((track) => track.stop());
      }

      setCurrentDeviceIndex((currentDeviceIndex) => {
        const currentDeviceIndexNew =
          currentDeviceIndex === devices.length - 1 ? 0 : currentDeviceIndex + 1;
        setCurrentDeviceInfo(null);
        getCurrentDeviceInfo(
          devices[currentDeviceIndexNew].deviceId,
          getImageCaptureResolutionHint(),
        ).then((currentDeviceInfo) => {
          if (currentDeviceInfo) {
            setCurrentDeviceInfo(currentDeviceInfo);
            setIsDeviceLoading(false);
          }
        });

        return currentDeviceIndexNew;
      });
    }
  };

  const getVideoConstraints = (deviceInfo: DeviceInfo): MediaTrackConstraints => {
    const videoConstraints = {
      deviceId: deviceInfo.deviceId,
    } as MediaTrackConstraints;

    if (deviceInfo.video.captureResolution) {
      videoConstraints.width = { ideal: deviceInfo.video.captureResolution.width };
      videoConstraints.height = { ideal: deviceInfo.video.captureResolution.height };
    }

    return videoConstraints;
  };

  const getWebcamProps = (): WebcamProps => {
    const isFacingUser = !!currentDeviceInfo?.video?.isFacingUser;

    const webCamProps: WebcamProps = {
      audio: false,
      forceScreenshotSourceSize: false,
      imageSmoothing: false,
      mirrored:
        props.imageMirrorBehavior === ImageMirrorBehavior.FLIP_WHEN_USER_FACING
          ? isFacingUser
          : false,
      screenshotFormat: 'image/jpeg',
      videoConstraints: currentDeviceInfo ? getVideoConstraints(currentDeviceInfo) : {},
      onUserMedia: () => {
        setIsDeviceLoading(false);
      },
      onUserMediaError: () => {
        // this shouldn't happen if onDeviceAccessDenied is passed in and handled correctly
      },
      screenshotQuality: 1,
    };

    if (props.imageDisplayResolution) {
      // @ts-ignore
      webCamProps.height = props.imageDisplayResolution.height;
      // @ts-ignore
      webCamProps.width = props.imageDisplayResolution.width;
    }

    if (props.imageCaptureResolutionHint?.type === ResolutionOptionType.MAXIMUM) {
      // @ts-ignore
      webCamProps.forceScreenshotSourceSize = true;
    }

    return webCamProps;
  };

  if (!props.children) {
    return <></>;
  }

  return props.children({
    renderFunctions: {
      drawCameraDisplay: () => <Webcam ref={webcamRef} {...getWebcamProps()} />,
    },
    commands: {
      refreshDeviceList: refreshDeviceList,
      switchDevice: cycleCamera,
      captureImage: capture,
    },
    status: {
      DeviceInfo: currentDeviceInfo
        ? {
            deviceId: currentDeviceInfo ? currentDeviceInfo.deviceId : '',
            video: {
              isFacingUser: currentDeviceInfo?.video?.isFacingUser
                ? currentDeviceInfo.video.isFacingUser
                : false,
              captureResolution: currentDeviceInfo
                ? {
                    width: currentDeviceInfo.video.captureResolution?.width || 0,
                    height: currentDeviceInfo.video.captureResolution?.height || 0,
                  }
                : undefined,
            },
          }
        : undefined,
      isDeviceLoading: isDeviceLoading,
      isRefreshingDeviceList: isDeviceListRefreshing,
      devicesTotalCount: devices?.length || 0,
    },
  });
}
